import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/contexts/ToastContext';
import ThemeToggle from '@/components/Theme/ThemeToggle';
import { useTheme } from '@/contexts/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import Avatar from '@/components/Avatar/Avatar';
import { usePWA } from '@/hooks/usePWA';

interface NavigationItem {
  name: string;
  href: string;
  icon: string;
  adminOnly?: boolean;
}

export default function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, signOut, isLoading } = useAuth();
  const { addToast } = useToast();
  const { theme, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const { isInstallable, installApp } = usePWA();

  console.log('🔄 Navigation rendering:', {
    hasUser: !!user,
    hasProfile: !!profile,
    userRole: profile?.role,
    isLoading,
    userEmail: user?.email,
    profileEmail: profile?.email
  });

  const navigation: NavigationItem[] = [
    { name: 'Dashboard', href: '/', icon: 'fas fa-home' },
    { name: 'Tables', href: '/tables', icon: 'fas fa-chair' },
    { name: 'Orders', href: '/orders', icon: 'fas fa-receipt' },
    { name: 'Menu', href: '/menu', icon: 'fas fa-utensils' },
    { name: 'Analytics', href: '/analytics', icon: 'fas fa-chart-line' },
    { name: 'Expenses', href: '/expenses', icon: 'fas fa-money-bill' },
    { name: 'Calendar', href: '/calendar', icon: 'fas fa-calendar' },
  ];

  const handleSignOut = async () => {
    try {
      await signOut();
      addToast({
        type: 'success',
        message: 'Successfully signed out',
      });
      navigate('/login');
    } catch (error) {
      addToast({
        type: 'error',
        message: 'Failed to sign out',
      });
    }
  };

  const toggleTheme = () => {
    setTheme(current => 
      current === 'light' ? 'dark' : 'light'
    );
  };

  if (isLoading) {
    return (
      <nav className="sticky top-0 z-50 w-full bg-white/60 backdrop-blur-sm border-b dark:border-dark-border dark:bg-dark-bg-light/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-14 items-center">
            <div className="flex justify-between w-full">
              <div className="flex-shrink-0 flex items-center">
                <span className="text-2xl font-extrabold bg-gradient-to-r from-primary-500 to-primary-700 bg-clip-text text-transparent">
                  Nestor
                </span>
              </div>
              <div className="flex items-center">
                <ThemeToggle />
              </div>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  const filteredNavigation = navigation.filter(item => {
    const shouldShow = !item.adminOnly || profile?.role === 'admin';
    /* console.log(`📍 Navigation item "${item.name}":`, {
      adminOnly: item.adminOnly,
      userRole: profile?.role,
      shouldShow,
      profileExists: !!profile
    }); */
    return shouldShow;
  });

  return (
    <nav className="sticky top-0 z-50 w-full bg-white/60 backdrop-blur-sm border-b dark:border-dark-border dark:bg-dark-bg-light/60">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-14 items-center justify-between">
          {/* Logo and hamburger menu */}
          <div className="flex items-center">
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-primary-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-primary-400 dark:hover:bg-dark-bg/50"
              onClick={() => setIsOpen(!isOpen)}
            >
              <span className="sr-only">Open main menu</span>
              <i className={`fas ${isOpen ? 'fa-times' : 'fa-bars'} w-5 h-5`} />
            </button>
            <Link to="/" className="flex items-center ml-2 md:ml-0">
              <img
                className="h-8 w-auto"
                src="/favicon-192x192.png"
                alt="Nestor"
              />
              <span className="ml-2 text-xl font-semibold text-gray-900 dark:text-white font-sans">
                Nestor
              </span>
            </Link>
          </div>

          {/* Desktop navigation */}
          <div className="hidden md:flex md:items-center md:space-x-2">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center px-2 py-2 rounded-md text-sm font-medium ${
                  location.pathname === item.href
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-700 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400'
                }`}
                title={item.name}
              >
                <i className={`${item.icon} w-5 h-5`} />
              </Link>
            ))}
          </div>

          {/* Right side items */}
          <div className="flex items-center space-x-4">
            {isInstallable && (
              <button
                onClick={installApp}
                className="p-2 text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300"
                title="Install App"
              >
                <i className="fas fa-download" />
              </button>
            )}
            <ThemeToggle />
            <Menu as="div" className="relative">
              <Menu.Button className="flex items-center">
                <Avatar email={profile?.email || user?.email || ''} className="h-8 w-8" />
              </Menu.Button>
              <Transition
                as={Menu.Items}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
                className="absolute right-0 mt-2 w-64 origin-top-right rounded-lg bg-gray-900/90 dark:bg-gray-900/90 backdrop-blur-sm shadow-lg ring-1 ring-white/10 focus:outline-none divide-y divide-white/10"
              >
                {/* User Info */}
                <div className="px-4 py-3">
                  <p className="text-sm text-white font-medium truncate">
                    {profile?.email || user?.email}
                  </p>
                  <p className="text-xs text-gray-400 mt-1 capitalize">
                    {profile?.role || 'User'}
                  </p>
                </div>

                {/* Menu Items */}
                <div className="py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/profile"
                        className={`${
                          active ? 'bg-gray-800/50 text-primary-400' : 'text-gray-300'
                        } group flex w-full items-center px-4 py-2 text-sm hover:text-primary-400 transition-colors duration-150`}
                      >
                        <i className="fas fa-user w-5 h-5 mr-3" />
                        Profile
                      </Link>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/settings"
                        className={`${
                          active ? 'bg-gray-800/50 text-primary-400' : 'text-gray-300'
                        } group flex w-full items-center px-4 py-2 text-sm hover:text-primary-400 transition-colors duration-150`}
                      >
                        <i className="fas fa-cog w-5 h-5 mr-3" />
                        Settings
                      </Link>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleSignOut}
                        className={`${
                          active ? 'bg-gray-800/50 text-primary-400' : 'text-gray-300'
                        } group flex w-full items-center px-4 py-2 text-sm hover:text-primary-400 transition-colors duration-150`}
                      >
                        <i className="fas fa-sign-out-alt w-5 h-5 mr-3" />
                        Sign out
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </Transition>
            </Menu>
          </div>
        </div>
      </div>

      {/* Mobile navigation */}
      <Transition
        show={isOpen}
        enter="transition ease-out duration-100 transform"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-75 transform"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <div className="md:hidden">
          <div className="bg-white/60 backdrop-blur-sm dark:bg-dark-bg-light/60 border-b dark:border-dark-border">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block rounded-md px-3 py-2 text-base font-medium ${
                    location.pathname === item.href
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  <i className={`${item.icon} mr-2`} />
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </Transition>
    </nav>
  );
}